import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiService } from '@/services/api';
import { Trash2, ShieldAlert } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { tagKeys } from '@/hooks/useTags';
import { entityTagsQueryKeys } from '@/hooks/useEntityTags';

 type Entity = 'deals' | 'contacts' | 'companies' | 'products' | 'tags' | 'pipelines';

 const actions: Array<{ key: Entity; title: string; desc: string }> = [
  { key: 'deals', title: 'Purger les Deals', desc: 'Supprime TOUS les deals et enregistrements associés (documents, tâches, activités, notifications, produits...).' },
  { key: 'contacts', title: 'Purger les Contacts', desc: 'Supprime TOUS les contacts et leurs associations (tags, communications, activités...).' },
  { key: 'companies', title: 'Purger les Companies', desc: 'Supprime TOUTES les companies et nettoie les relations.' },
  { key: 'products', title: 'Purger les Produits', desc: 'Supprime TOUS les produits et associations avec les deals.' },
  { key: 'pipelines', title: 'Purger les Pipelines', desc: 'Supprime TOUS les pipelines, leurs étapes et TOUS les deals associés, ainsi que les enregistrements liés.' },
  { key: 'tags', title: 'Purger les Tags', desc: 'Supprime TOUS les tags et leurs associations aux entités.' },
 ];

 export default function SuperAdminPurge() {
  const [selected, setSelected] = useState<Entity | null>(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState<boolean>(false);
  const [result, setResult] = useState<Record<string, number> | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const requiredPhrase = useMemo(() => (selected ? `PURGE ${selected}` : ''), [selected]);

  async function executePurge() {
    if (!selected) return;
    setBusy(true);
    setResult(null);
    try {
      const res =
        selected === 'deals' ? await apiService.purgeDeals() :
        selected === 'contacts' ? await apiService.purgeContacts() :
        selected === 'companies' ? await apiService.purgeCompanies() :
        selected === 'products' ? await apiService.purgeProducts() :
        selected === 'pipelines' ? await apiService.purgePipelines() :
        await apiService.purgeTags();
      setResult(res.deleted || null);
      toast({ title: 'Purge effectuée', description: 'Les éléments sélectionnés ont été supprimés.' });
      // Après purge des tags, invalider et refetcher tous les caches liés aux tags
      if (selected === 'tags') {
        try {
          await Promise.all([
            // Invalide toutes les requêtes "tags" (listes, détails, hiérarchie, résumé, stats, recherches...)
            queryClient.invalidateQueries(tagKeys.all, { refetchType: 'active' }),
            // Invalide les caches d'association tags<->entités (version avec tiret)
            queryClient.invalidateQueries(entityTagsQueryKeys.all, { refetchType: 'active' }),
            // Invalide les caches d'association tags<->entités (version camelCase utilisée dans certains hooks)
            queryClient.invalidateQueries(['entityTags'], { refetchType: 'active' }),
          ]);
        } catch {
          // no-op: l'invalidation ne doit pas bloquer l'UX
        }
      }
      // Après purge des pipelines: invalider deals et pipelines côté client et nettoyer les préférences locales
      if (selected === 'pipelines') {
        try {
          await Promise.all([
            queryClient.invalidateQueries(['deals'], { refetchType: 'active' }),
            queryClient.invalidateQueries(['deals', 'pipelines'], { refetchType: 'active' }),
          ]);
        } catch {
          // no-op
        }
        // Nettoyer la sélection persistée du pipeline pour éviter les références invalides
        try {
          localStorage.removeItem('crm_default_pipeline_id');
          localStorage.removeItem('crm_last_pipeline_id');
        } catch {}
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    } finally {
      setBusy(false);
      setConfirm('');
      setSelected(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-red-500" />
            Zone de danger • Purge globale
          </h1>
          <p className="text-muted-foreground">Actions critiques et irréversibles. Réservé aux Super Admins.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {actions.map((a) => (
          <Card key={a.key} className="border-red-200/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" /> {a.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{a.desc}</p>
              <Button
                variant="destructive"
                disabled={busy}
                onClick={() => setSelected(a.key)}
              >
                {busy && selected === a.key ? 'Purge en cours…' : a.title}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {result && (
        <Card className="border-green-200/50">
          <CardHeader>
            <CardTitle className="text-green-700">Résultat de la purge</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted/50 p-3 rounded md:max-h-72 overflow-auto">{JSON.stringify(result, null, 2)}</pre>
          </CardContent>
        </Card>
      )}

      <Dialog open={selected !== null} onOpenChange={(open) => { if (!open) { setSelected(null); setConfirm(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la purge {selected ? `« ${selected} »` : ''}</DialogTitle>
            <DialogDescription>
              Tapez « {requiredPhrase} » pour confirmer. Cette action est irréversible et supprime des données de manière permanente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Phrase de confirmation</label>
            <Input placeholder={requiredPhrase} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelected(null); setConfirm(''); }} disabled={busy}>Annuler</Button>
            <Button variant="destructive" onClick={executePurge} disabled={busy || confirm !== requiredPhrase}>
              Confirmer la purge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
